import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import api from '../api/client';
import { Role } from '../lib/roles';

export interface CompanyInfo {
  id: string;
  name: string;
  cnpj?: string | null;
  phone?: string | null;
  status?: string | null;
  plan?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
}

export interface ImpersonationInfo {
  userId: string;
  email: string;
}

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  active: boolean;
  mustChangePassword: boolean;
  impersonatedBy: ImpersonationInfo | null;
  company: CompanyInfo;
}

interface AuthContextValue {
  token: string | null;
  me: CurrentUser | null;
  login: (token: string) => void;
  logout: () => void;
  reloadMe: () => Promise<void>;
  hasRole: (...allowed: Role[]) => boolean;
  isSuperAdmin: () => boolean;
  isImpersonating: () => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [me, setMe] = useState<CurrentUser | null>(null);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get<CurrentUser>('/auth/me');
      setMe(data);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => {
    if (token) loadMe();
    else setMe(null);
  }, [token, loadMe]);

  const login = (newToken: string) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
  };

  const reloadMe = async () => {
    await loadMe();
  };

  const hasRole = (...allowed: Role[]) => {
    if (!me) return false;
    if (me.role === 'SUPER_ADMIN') return true;
    return allowed.includes(me.role);
  };
  const isSuperAdmin = () => me?.role === 'SUPER_ADMIN';
  const isImpersonating = () => Boolean(me?.impersonatedBy);

  return (
    <AuthContext.Provider
      value={{ token, me, login, logout, reloadMe, hasRole, isSuperAdmin, isImpersonating }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}
